import React, {useCallback, useEffect, useRef, useState} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import SafeIcon from '../../../common/SafeIcon';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

const {FiChevronLeft, FiChevronRight, FiImage, FiX} = FiIcons;

const DEFAULT_PRESENTATION_URL_ES =
  'https://media.publit.io/file/ProsperityWebApp/Presentacion-GFI-En-Espanol.pdf';
const DEFAULT_PRESENTATION_URL_EN =
  'https://media.publit.io/file/ProsperityWebApp/GFI-Overview-Presentation.pdf';

const DOT_CUTOFF = 12;

function isGoogleSlidesEmbed(url) {
  return /docs\.google\.com\/presentation\/.+\/embed/i.test(url || '');
}

function isDirectPdfSource(url) {
  if (!url) return false;
  const u = String(url).trim().toLowerCase();
  if (u.startsWith('blob:')) return true;
  return /\.pdf($|\?|#)/i.test(u);
}

function padPage(n) {
  return String(n).padStart(2, '0');
}

function PresentationDeck({language, name, onClose, source: sourceProp}) {
  const [index, setIndex] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [iframeUrl, setIframeUrl] = useState('');
  const objectUrlRef = useRef(null);

  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const pdfDocRef = useRef(null);
  const renderTaskRef = useRef(null);

  const applySource = useCallback((src) => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    setIndex(0);
    setError(null);
    setNumPages(0);

    if (!src) {
      setPdfUrl(null);
      setIframeUrl('');
      return;
    }

    if (typeof File !== 'undefined' && src instanceof File) {
      const url = URL.createObjectURL(src);
      objectUrlRef.current = url;
      setPdfUrl(url);
      setIframeUrl('');
      return;
    }

    const url = String(src).trim();
    if (isDirectPdfSource(url)) {
      setPdfUrl(url);
      setIframeUrl('');
    } else {
      setPdfUrl(null);
      setIframeUrl(url);
    }
  }, []);

  useEffect(() => {
    if (sourceProp) {
      applySource(sourceProp);
    } else {
      applySource(DEFAULT_PRESENTATION_URL_ES);
    }
  }, [sourceProp, applySource]);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  const renderPage = useCallback(async (pageNum) => {
    const pdf = pdfDocRef.current;
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!pdf || !canvas || !wrap) return;

    if (renderTaskRef.current) {
      try {
        renderTaskRef.current.cancel();
      } catch {
        /* ignore */
      }
      renderTaskRef.current = null;
    }

    const page = await pdf.getPage(pageNum);
    const base = page.getViewport({scale: 1});
    const availW = Math.max(120, wrap.clientWidth - 8);
    const availH = Math.max(120, wrap.clientHeight - 8);
    const scale = Math.min(availW / base.width, availH / base.height);
    const viewport = page.getViewport({scale: Math.max(0.2, scale)});

    const outputScale = window.devicePixelRatio || 1;
    canvas.width = Math.floor(viewport.width * outputScale);
    canvas.height = Math.floor(viewport.height * outputScale);
    canvas.style.width = `${Math.floor(viewport.width)}px`;
    canvas.style.height = `${Math.floor(viewport.height)}px`;

    const ctx = canvas.getContext('2d');
    const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null;
    const task = page.render({canvasContext: ctx, viewport, transform});
    renderTaskRef.current = task;
    try {
      await task.promise;
    } catch (err) {
      if (err?.name !== 'RenderingCancelledException') throw err;
    } finally {
      if (renderTaskRef.current === task) renderTaskRef.current = null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!pdfUrl) {
        pdfDocRef.current = null;
        setNumPages(0);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        if (pdfDocRef.current) {
          try {
            await pdfDocRef.current.destroy();
          } catch {
            /* ignore */
          }
          pdfDocRef.current = null;
        }
        const pdf = await pdfjsLib.getDocument({url: pdfUrl, withCredentials: false}).promise;
        if (cancelled) {
          await pdf.destroy();
          return;
        }
        pdfDocRef.current = pdf;
        setNumPages(pdf.numPages || 0);
        setIndex(0);
      } catch (err) {
        console.error(err);
        if (!cancelled) setError(err?.message || 'load failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [pdfUrl]);

  useEffect(() => {
    if (!pdfUrl || !numPages || loading || error) return undefined;
    let cancelled = false;
    (async () => {
      try {
        if (!cancelled) await renderPage(index + 1);
      } catch (err) {
        if (!cancelled) console.error(err);
      }
    })();
    const ro = new ResizeObserver(() => {
      renderPage(index + 1).catch(() => {});
    });
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => {
      cancelled = true;
      ro.disconnect();
    };
  }, [pdfUrl, numPages, index, loading, error, renderPage]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (!pdfUrl || !numPages) return;
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        setIndex((value) => Math.min(numPages - 1, value + 1));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setIndex((value) => Math.max(0, value - 1));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pdfUrl, numPages]);

  useEffect(() => {
    return () => {
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch {
          /* ignore */
        }
      }
      if (pdfDocRef.current) {
        pdfDocRef.current.destroy().catch(() => {});
        pdfDocRef.current = null;
      }
    };
  }, []);

  const mode = pdfUrl ? 'pdf' : iframeUrl ? 'iframe' : 'empty';
  const googleHint = mode === 'iframe' && isGoogleSlidesEmbed(iframeUrl);
  const useDots = mode === 'pdf' && numPages > 0 && numPages <= DOT_CUTOFF;

  return (
    <motion.div
      className="deck-overlay"
      initial={{opacity: 0}}
      animate={{opacity: 1}}
      exit={{opacity: 0}}
    >
      <header className="deck-header">
        <div>
          <span>PROSPERITY LAUNCH</span>
          <b>{name}</b>
        </div>
        <div className="deck-header-tools">
          <label className="upload-field">
            <SafeIcon icon={FiImage} />
            <span>{language === 'es' ? 'Subir PDF' : 'Open PDF'}</span>
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) applySource(file);
                e.target.value = '';
              }}
            />
          </label>
          <button
            type="button"
            className="quiet-load"
            onClick={() => applySource(DEFAULT_PRESENTATION_URL_ES)}
          >
            ES Default
          </button>
          <button
            type="button"
            className="quiet-load"
            onClick={() => applySource(DEFAULT_PRESENTATION_URL_EN)}
          >
            EN Default
          </button>
          <button
            type="button"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onClose();
            }}
            title="Close presentation"
          >
            <SafeIcon icon={FiX} />
          </button>
        </div>
      </header>

      <AnimatePresence mode="wait">
        <motion.section
          className={`deck-slide${mode !== 'empty' ? ' deck-slide-media' : ''}`}
          key={`${mode}-${pdfUrl || iframeUrl || 'empty'}-${index}`}
          initial={{opacity: 0, x: 40}}
          animate={{opacity: 1, x: 0}}
          exit={{opacity: 0, x: -40}}
          transition={{duration: 0.4}}
        >
          {mode === 'pdf' && numPages > 0 && (
            <span className="slide-count">
              {padPage(index + 1)} / {padPage(numPages)}
            </span>
          )}

          {mode === 'empty' && (
            <div className="deck-status">
              {language === 'es'
                ? 'Carga un PDF o elige el default ES/EN para comenzar.'
                : 'Open a PDF or load the ES/EN default to begin.'}
            </div>
          )}

          {mode === 'pdf' && loading && (
            <div className="deck-status">
              {language === 'es' ? 'Cargando diapositivas…' : 'Loading slides…'}
            </div>
          )}

          {mode === 'pdf' && error && !loading && (
            <div className="deck-status">
              {language === 'es'
                ? 'No se pudo cargar este PDF.'
                : 'Could not load this PDF.'}
            </div>
          )}

          {mode === 'pdf' && !loading && !error && numPages > 0 && (
            <div className="deck-canvas-wrap" ref={wrapRef}>
              <canvas ref={canvasRef} />
            </div>
          )}

          {mode === 'iframe' && (
            <>
              <iframe className="deck-iframe" title="presentation-embed" src={iframeUrl} allowFullScreen />
              {googleHint && (
                <p className="deck-iframe-hint">
                  {language === 'es'
                    ? 'Haz clic en la presentación y usa las flechas para navegar'
                    : 'Click the slide once, then use arrow keys to navigate'}
                </p>
              )}
            </>
          )}
        </motion.section>
      </AnimatePresence>

      <footer className="deck-navigation">
        <button
          type="button"
          disabled={mode !== 'pdf' || index === 0}
          onClick={() => setIndex(value => value - 1)}
        >
          <SafeIcon icon={FiChevronLeft} />
        </button>
        <div>
          {mode === 'pdf' && numPages > 0 ? (
            useDots ? (
              Array.from({length: numPages}).map((_, slideIndex) => (
                <i key={slideIndex} className={slideIndex === index ? 'active' : ''} />
              ))
            ) : (
              <span className="deck-page-text">
                {index + 1} / {numPages}
              </span>
            )
          ) : (
            <span className="deck-page-text">—</span>
          )}
        </div>
        <button
          type="button"
          disabled={mode !== 'pdf' || index >= numPages - 1}
          onClick={() => setIndex(value => value + 1)}
        >
          <SafeIcon icon={FiChevronRight} />
        </button>
      </footer>
    </motion.div>
  );
}

export default PresentationDeck;
